import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Plus,
  FileText,
  AlertTriangle,
  History,
  Eye,
  Pencil,
  Trash2,
  MoreVertical,
  Share2,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Search,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  FolderPlus,
  Table2,
  Presentation,
  Mail,
  MoreHorizontal,
  Folder,
} from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { templateService, type TemplateOData } from '@/lib/api/services/templates'
import { hubService } from '@/lib/api/services/hubs'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { FluentCard } from '@/components/layout/page-header'
import { ShareDialog } from '@/components/templates/sharing'
import { VersionHistoryTableDialog } from '@/components/templates/VersionHistoryTableDialog'
import { CreateHubDialog, EditHubDialog } from '@/components/hubs'
import { useAuth } from '@/lib/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { TemplateHubTree, TemplateHub, CreateHubRequest, UpdateHubRequest } from '@/lib/api/types/hubs'

interface DeleteDialogState {
  open: boolean
  template: TemplateOData | null
}

interface VersionsDialogState {
  open: boolean
  template: TemplateOData | null
}

interface SharingDialogState {
  open: boolean
  template: TemplateOData | null
}

interface DeleteFolderDialogState {
  open: boolean
  folder: TemplateHubTree | null
}

// Helper to get initials from folder name
function getFolderInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length === 0 || !words[0]) return '??'
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase()
}

// Helper to get background color from hex color
function getColorStyle(color?: string): React.CSSProperties {
  if (!color) return { backgroundColor: '#6366F1' }
  return { backgroundColor: color }
}

// Type icon helper
function TemplateTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'Word':
      return <FileText className="h-4 w-4 text-blue-600" />
    case 'Excel':
      return <Table2 className="h-4 w-4 text-green-600" />
    case 'PowerPoint':
      return <Presentation className="h-4 w-4 text-orange-600" />
    case 'Email':
      return <Mail className="h-4 w-4 text-purple-600" />
    default:
      return <FileText className="h-4 w-4 text-gray-500" />
  }
}

// Folder Tree Item Component
interface FolderTreeItemProps {
  folder: TemplateHubTree
  level: number
  selectedFolderId: string | null
  expandedIds: Set<string>
  onSelect: (folderId: string | null) => void
  onToggleExpand: (folderId: string) => void
  onEdit: (folder: TemplateHubTree) => void
  onDelete: (folder: TemplateHubTree) => void
  onAddSubfolder: (parentId: string) => void
}

function FolderTreeItem({
  folder,
  level,
  selectedFolderId,
  expandedIds,
  onSelect,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddSubfolder,
}: FolderTreeItemProps) {
  const isExpanded = expandedIds.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const hasChildren = folder.children && folder.children.length > 0

  return (
    <div className="group">
      <div
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          isSelected && 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-medium'
        )}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(folder.id)
            }}
            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Folder Icon */}
        <button
          onClick={() => onSelect(folder.id)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <div
            className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
            style={getColorStyle(folder.color)}
          >
            {getFolderInitials(folder.name)}
          </div>
          <span className="truncate flex-1 text-left">{folder.name}</span>
        </button>

        {/* Template Count */}
        <span className="text-xs text-gray-400 tabular-nums mr-1">{folder.templateCount}</span>

        {/* Context Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity"
            >
              <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onAddSubfolder(folder.id)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Subfolder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(folder)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(folder)}
              className="text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children?.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSubfolder={onAddSubfolder}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function TemplateLibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedFolderId = searchParams.get('folder')

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sidebarSearch, setSidebarSearch] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Folders state
  const [folders, setFolders] = useState<TemplateHubTree[]>([])
  const [foldersLoading, setFoldersLoading] = useState(true)
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false)
  const [createFolderParentId, setCreateFolderParentId] = useState<string | undefined>(undefined)
  const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false)
  const [selectedFolderForEdit, setSelectedFolderForEdit] = useState<TemplateHub | null>(null)
  const [deleteFolderDialog, setDeleteFolderDialog] = useState<DeleteFolderDialogState>({ open: false, folder: null })
  const [deleteFolderLoading, setDeleteFolderLoading] = useState(false)

  // Templates state
  const [templates, setTemplates] = useState<TemplateOData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, template: null })
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [versionsDialog, setVersionsDialog] = useState<VersionsDialogState>({ open: false, template: null })
  const [sharingDialog, setSharingDialog] = useState<SharingDialogState>({ open: false, template: null })

  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()

  // Get selected folder details
  const selectedFolder = useMemo(() => {
    if (!selectedFolderId) return null
    const findFolder = (folders: TemplateHubTree[]): TemplateHubTree | null => {
      for (const folder of folders) {
        if (folder.id === selectedFolderId) return folder
        if (folder.children) {
          const found = findFolder(folder.children)
          if (found) return found
        }
      }
      return null
    }
    return findFolder(folders)
  }, [selectedFolderId, folders])

  // Calculate total templates across all folders
  const totalTemplatesInFolders = useMemo(() => {
    const countAll = (folders: TemplateHubTree[]): number =>
      folders.reduce((sum, folder) => sum + folder.templateCount + countAll(folder.children || []), 0)
    return countAll(folders)
  }, [folders])

  // Filter folders by search
  const filteredFolders = useMemo(() => {
    if (!sidebarSearch.trim()) return folders
    const search = sidebarSearch.toLowerCase()
    const filterTree = (folders: TemplateHubTree[]): TemplateHubTree[] => {
      return folders
        .map((folder) => ({
          ...folder,
          children: folder.children ? filterTree(folder.children) : [],
        }))
        .filter(
          (folder) =>
            folder.name.toLowerCase().includes(search) ||
            folder.description?.toLowerCase().includes(search) ||
            folder.children.length > 0
        )
    }
    return filterTree(folders)
  }, [folders, sidebarSearch])

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  // Load folders
  const loadFolders = useCallback(async () => {
    try {
      setFoldersLoading(true)
      const tree = await hubService.getHubTree()
      setFolders(tree)
      // Auto-expand first level
      setExpandedIds(new Set(tree.map((h) => h.id)))
    } catch (err) {
      console.error('Failed to load folders:', err)
    } finally {
      setFoldersLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFolders()
  }, [loadFolders])

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // If a folder is selected, load templates from that folder's membership
      if (selectedFolderId) {
        const folderTemplates = await hubService.getHubTemplates(selectedFolderId)

        // Transform folder membership data to TemplateOData format
        let transformedTemplates: TemplateOData[] = folderTemplates.map((ht) => ({
          id: ht.templateId,
          name: ht.templateName,
          description: '', // Not available in membership
          type: ht.templateType as 'Word' | 'Excel' | 'PowerPoint' | 'Outlook',
          content: '',
          variables: [],
          tags: [],
          thumbnailUrl: ht.thumbnailUrl,
          isActive: ht.isActive,
          currentVersionNumber: 1, // Not available in membership
          createdAt: ht.addedAt, // Use addedAt as fallback
          updatedAt: ht.addedAt,
          tenantId: '',
          createdBy: ht.addedBy || '',
          updatedBy: '',
        }))

        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase()
          transformedTemplates = transformedTemplates.filter((t) =>
            t.name.toLowerCase().includes(searchLower)
          )
        }

        // Apply sorting
        if (sortField) {
          transformedTemplates.sort((a, b) => {
            const aVal = a[sortField as keyof TemplateOData]
            const bVal = b[sortField as keyof TemplateOData]
            if (aVal == null) return 1
            if (bVal == null) return -1
            const cmp = String(aVal).localeCompare(String(bVal))
            return sortDirection === 'desc' ? -cmp : cmp
          })
        }

        // Apply pagination
        const totalFiltered = transformedTemplates.length
        const startIdx = (page - 1) * pageSize
        const paginatedTemplates = transformedTemplates.slice(startIdx, startIdx + pageSize)

        setTemplates(paginatedTemplates)
        setTotalCount(totalFiltered)
      } else {
        // Load all templates using OData
        const params = getODataParams()
        const response = await templateService.getTemplates(params)
        setTemplates(response.value || [])
        setTotalCount(response['@odata.count'] || response.value?.length || 0)
      }
    } catch (_error) {
      setError('Failed to load templates')
      toast({
        title: 'Error',
        description: 'Failed to load templates',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast, selectedFolderId, search, sortField, sortDirection, page, pageSize])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  // Folder selection
  const handleSelectFolder = (folderId: string | null) => {
    if (folderId) {
      searchParams.set('folder', folderId)
    } else {
      searchParams.delete('folder')
    }
    setSearchParams(searchParams)
    setPage(1)
  }

  // Toggle expand
  const handleToggleExpand = (folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  // Create folder
  const handleCreateFolder = async (data: CreateHubRequest) => {
    try {
      await hubService.createHub({
        ...data,
        parentId: createFolderParentId,
      })
      toast({
        title: 'Success',
        description: 'Folder created successfully',
      })
      loadFolders()
      setCreateFolderParentId(undefined)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create folder'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      throw err
    }
  }

  // Open create folder dialog
  const handleOpenCreateFolder = (parentId?: string) => {
    setCreateFolderParentId(parentId)
    setCreateFolderDialogOpen(true)
  }

  // Edit folder
  const handleOpenEditFolder = (folder: TemplateHubTree) => {
    setSelectedFolderForEdit(folder as TemplateHub)
    setEditFolderDialogOpen(true)
  }

  const handleUpdateFolder = async (id: string, data: UpdateHubRequest) => {
    try {
      await hubService.updateHub(id, data)
      toast({
        title: 'Success',
        description: 'Folder updated successfully',
      })
      loadFolders()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update folder'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      throw err
    }
  }

  // Delete folder
  const handleOpenDeleteFolder = (folder: TemplateHubTree) => {
    setDeleteFolderDialog({ open: true, folder })
  }

  const handleDeleteFolder = async () => {
    if (!deleteFolderDialog.folder) return

    setDeleteFolderLoading(true)
    try {
      await hubService.deleteHub(deleteFolderDialog.folder.id)
      toast({
        title: 'Success',
        description: 'Folder deleted successfully',
      })
      // If the deleted folder was selected, clear selection
      if (selectedFolderId === deleteFolderDialog.folder.id) {
        handleSelectFolder(null)
      }
      loadFolders()
      setDeleteFolderDialog({ open: false, folder: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete folder'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setDeleteFolderLoading(false)
    }
  }

  // Delete template
  const openDeleteDialog = (template: TemplateOData) => {
    setDeleteDialog({ open: true, template })
  }

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, template: null })
  }

  const handleDelete = async () => {
    if (!deleteDialog.template) return

    setDeleteLoading(true)
    try {
      await templateService.deleteTemplate(deleteDialog.template.id)
      toast({
        title: 'Success',
        description: 'Template moved to bin',
      })
      closeDeleteDialog()
      loadTemplates()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      })
    } finally {
      setDeleteLoading(false)
    }
  }

  // Versions dialog
  const openVersionsDialog = (template: TemplateOData) => {
    setVersionsDialog({ open: true, template })
  }

  const closeVersionsDialog = () => {
    setVersionsDialog({ open: false, template: null })
  }

  // Sharing dialog
  const openSharingDialog = (template: TemplateOData) => {
    setSharingDialog({ open: true, template })
  }

  const closeSharingDialog = () => {
    setSharingDialog({ open: false, template: null })
  }

  const columns: ColumnDef<TemplateOData>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (template) => (
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700">
            <TemplateTypeIcon type={template.type} />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{template.name}</span>
        </div>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      accessor: 'description',
      filterable: true,
      filterType: 'text',
      cell: (template) => (
        <span className="text-gray-500 dark:text-gray-400 truncate max-w-[200px] block">
          {template.description || '-'}
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Word', value: 'Word' },
        { label: 'Excel', value: 'Excel' },
        { label: 'PowerPoint', value: 'PowerPoint' },
        { label: 'Email', value: 'Email' },
      ],
      cell: (template) => (
        <Badge variant="outline" className="font-medium">
          {template.type}
        </Badge>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessor: 'isActive',
      sortable: true,
      filterable: true,
      filterType: 'boolean',
      cell: (template) => (
        <Badge
          variant={template.isActive ? 'default' : 'secondary'}
          className={cn(
            template.isActive
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : ''
          )}
        >
          {template.isActive ? 'Active' : 'Draft'}
        </Badge>
      ),
    },
    {
      id: 'currentVersionNumber',
      header: 'Version',
      accessor: 'currentVersionNumber',
      sortable: true,
      width: '100px',
      cell: (template) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <History className="h-3 w-3" />
          v{template.currentVersionNumber || 1}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      cell: (template) => (
        <span className="text-gray-500 dark:text-gray-400 text-sm">
          {new Date(template.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '80px',
      cell: (template) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <DropdownMenuItem
              onClick={() => navigate(`/templates/${template.id}`)}
              className="rounded-lg gap-2"
            >
              <Eye className="h-4 w-4" />
              View
            </DropdownMenuItem>
            <PermissionGate permissions={['templates.update']}>
              <DropdownMenuItem
                onClick={() => navigate(`/templates/${template.id}/edit`)}
                className="rounded-lg gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
            </PermissionGate>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                openVersionsDialog(template)
              }}
              className="rounded-lg gap-2"
            >
              <History className="h-4 w-4" />
              Versions
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                openSharingDialog(template)
              }}
              className="rounded-lg gap-2"
            >
              <Share2 className="h-4 w-4" />
              Share
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <PermissionGate permissions={['templates.delete']}>
              <DropdownMenuItem
                className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                onClick={() => openDeleteDialog(template)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6">
      {/* Sidebar - Folder Tree */}
      <div
        className={cn(
          'border-r border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col transition-all duration-300',
          sidebarOpen ? 'w-72' : 'w-0'
        )}
      >
        {sidebarOpen && (
          <>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Folder className="h-4 w-4 text-indigo-500" />
                  Folders
                </h2>
                <PermissionGate permissions={['templates.create']}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleOpenCreateFolder()}
                    title="Create Folder"
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </PermissionGate>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search folders..."
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>

            {/* Sidebar Content */}
            <ScrollArea className="flex-1">
              <div className="p-2">
                {/* All Templates */}
                <button
                  onClick={() => handleSelectFolder(null)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all mb-1',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    !selectedFolderId &&
                      'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-medium'
                  )}
                >
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <FileText className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="flex-1 text-left">All Templates</span>
                  <span className="text-xs text-gray-400 tabular-nums">{totalTemplatesInFolders || totalCount}</span>
                </button>

                {/* Divider */}
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />

                {/* Folder Tree */}
                {foldersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredFolders.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {sidebarSearch ? 'No folders found' : 'No folders yet'}
                    </p>
                    {!sidebarSearch && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleOpenCreateFolder()}
                        className="mt-1"
                      >
                        Create your first folder
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredFolders.map((folder) => (
                      <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        level={0}
                        selectedFolderId={selectedFolderId}
                        expandedIds={expandedIds}
                        onSelect={handleSelectFolder}
                        onToggleExpand={handleToggleExpand}
                        onEdit={handleOpenEditFolder}
                        onDelete={handleOpenDeleteFolder}
                        onAddSubfolder={handleOpenCreateFolder}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              {sidebarOpen ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </Button>
            <div className="flex-1">
              {/* Custom Header with Folder Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedFolder && (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-lg"
                      style={getColorStyle(selectedFolder.color)}
                    >
                      {getFolderInitials(selectedFolder.name)}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedFolder ? selectedFolder.name : 'Template Library'}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {selectedFolder
                        ? selectedFolder.description || `${selectedFolder.templateCount} templates in this folder`
                        : 'Browse and manage all your document templates'}
                    </p>
                  </div>
                </div>
                <PermissionGate permissions={['templates.create']}>
                  <Link
                    to="/templates/new"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20"
                  >
                    <Plus className="h-4 w-4" />
                    Create Template
                  </Link>
                </PermissionGate>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 sm:grid-cols-4 mb-6">
            {[
              { label: 'Total Templates', value: totalCount, color: 'from-indigo-400 to-indigo-600' },
              { label: 'Active', value: templates.filter((t) => t.isActive).length, color: 'from-emerald-400 to-emerald-600' },
              { label: 'Draft', value: templates.filter((t) => !t.isActive).length, color: 'from-gray-400 to-gray-600' },
              { label: 'Word Templates', value: templates.filter((t) => t.type === 'Word').length, color: 'from-blue-400 to-blue-600' },
            ].map((stat) => (
              <FluentCard key={stat.label} className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </FluentCard>
            ))}
          </div>
        </div>

        {/* Templates Table */}
        <div className="flex-1 overflow-hidden px-6 pb-6">
          <FluentCard className="h-full overflow-hidden flex flex-col">
            <DataTable
              columns={columns}
              data={templates}
              totalCount={totalCount}
              loading={loading}
              error={error}
              onRetry={loadTemplates}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={setSort}
              filters={filters}
              onFilterChange={setFilters}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search templates..."
              rowKey="id"
              onRowClick={(template) => navigate(`/templates/${template.id}`)}
              emptyMessage={
                selectedFolder
                  ? `No templates in "${selectedFolder.name}" folder`
                  : 'No templates found'
              }
            />
          </FluentCard>
        </div>
      </div>

      {/* Delete Template Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.template?.name}"? This template will be
              moved to the bin where you can restore it if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Folder Confirmation Dialog */}
      <AlertDialog
        open={deleteFolderDialog.open}
        onOpenChange={(open) => !open && setDeleteFolderDialog({ open: false, folder: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete Folder
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteFolderDialog.folder?.name}"?
              {deleteFolderDialog.folder?.children && deleteFolderDialog.folder.children.length > 0 && (
                <span className="block mt-2 text-orange-600 dark:text-orange-400">
                  Warning: This folder has {deleteFolderDialog.folder.children.length} subfolder(s) that will also be deleted.
                </span>
              )}
              {deleteFolderDialog.folder && deleteFolderDialog.folder.templateCount > 0 && (
                <span className="block mt-2 text-orange-600 dark:text-orange-400">
                  Warning: {deleteFolderDialog.folder.templateCount} template(s) are assigned to this folder.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteFolderLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={handleDeleteFolder}
              disabled={deleteFolderLoading}
            >
              {deleteFolderLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Versions Modal */}
      {versionsDialog.template && (
        <VersionHistoryTableDialog
          open={versionsDialog.open}
          onOpenChange={(open) => !open && closeVersionsDialog()}
          templateId={versionsDialog.template.id}
          templateName={versionsDialog.template.name}
          currentVersionNumber={versionsDialog.template.currentVersionNumber}
          onVersionRestored={loadTemplates}
        />
      )}

      {/* Sharing Dialog */}
      {sharingDialog.template && (
        <ShareDialog
          open={sharingDialog.open}
          onOpenChange={(open) => !open && closeSharingDialog()}
          templateId={sharingDialog.template.id}
          templateName={sharingDialog.template.name}
          currentUserId={user?.id}
        />
      )}

      {/* Create Folder Dialog */}
      <CreateHubDialog
        open={createFolderDialogOpen}
        onOpenChange={setCreateFolderDialogOpen}
        onSubmit={handleCreateFolder}
        parentHubs={folders}
        initialParentId={createFolderParentId}
      />

      {/* Edit Folder Dialog */}
      <EditHubDialog
        open={editFolderDialogOpen}
        onOpenChange={setEditFolderDialogOpen}
        hub={selectedFolderForEdit}
        onSubmit={handleUpdateFolder}
        parentHubs={folders}
      />
    </div>
  )
}
